import ReactDOM from "react-dom/client";
import PlayerInfo from "./components/PlayerInfo/PlayerInfo";
import MediaWidget from "./components/MediaWidget/MediaWidget";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLoaderData,
  useLocation,
} from "react-router-dom";
import { Header as AntHeader, Content } from "antd/es/layout/layout";
import PlayerControl from "./components/PlayerControl/PlayerControl";
import ConfigurationPage from "./components/ConfigurationPage/ConfigurationPage";
import "./index.css";
import "./ant-override.css";
import "./ant.css";
import "@fontsource/material-symbols-sharp";
import "@fontsource/play";
import { config } from "./config";
import { log, setLoglevel } from "./logging";
import auth, { separateWidgetAuth } from "./auth";
import axios from "axios";
import type { Params } from "react-router-dom";
import PaymentGatewaysConfiguration from "./pages/PaymentGatewaysConfiguration/PaymentGatewaysConfiguration";
import { WidgetData } from "./types/WidgetData";
import { HistoryPage } from "./pages/History/HistoryPage";
import { ConfigProvider, Flex, theme } from "antd";
import "./i18n";
import "animate.css";
import WidgetWrapper from "./WidgetWrapper";
import Header from "./components/ConfigurationPage/Header";
import Toolbar, { Page } from "./components/ConfigurationPage/Toolbar";
import DonatonPage from "./pages/Donaton/DonatonPage";
import CanvasPage from "./pages/Canvas/CanvasPage";
import PaymentPageConfigPage from "./pages/PaymentPageConfig/PaymentPageConfigPage";
import DonationTimerPage from "./pages/DonationTimer/DonationTimerPage";
import PlayerPopupPage from "./pages/PlayerPopup/PlayerPopupPage";
import DonationGoalPage from "./pages/DonationGoal/DonationGoalPage";
import DonatersTopListPage from "./pages/DonatersTopList/DonatersTopListPage";
import RutonyChatPage from "./pages/RutonyChat/RutonyChatPage";
import Login from "./pages/Login/Login";
import PaymentAlertsPage from "./pages/Alerts/PaymentAlertsPage";
import AutomationPage from "./pages/Automation/AutomationPage";
import CommandsRewardsPage from "./pages/CommandsRewards/CommandsRewardsPage";
import LoyaltyPage from "./pages/Loyalty/LoyaltyPage";
import { GuidesPage } from "./pages/Guides/GuidesPage";
import { IntegrationsPage } from "./pages/Integrations/IntegrationsPage";
import UtilityButton from "./components/Button/UtilityButton";
import WarningsPanel from "./components/WarningsPanel/WarningsPanel";
import HorizontalEventsPage from "./pages/HorizontalEvents/HorizontalEventsPage";
import { FontContext, FontStore } from "./stores/FontStore";
import RouletteWidgetPage from "./pages/Roulette/RouletteWidgetPage";
import { useState } from "react";
import {
  DefaultWidgetStore,
  WidgetStore,
  WidgetStoreContext,
} from "./stores/WidgetStore";
import {
  PaymentPageConfig,
  PaymentPageConfigContext,
} from "./components/MediaWidget/PaymentPageConfig";
import HistoryWidgetPage from "./pages/History/HistoryWidgetPage";
import ReelWidgetPage from "./pages/Reel/ReelWidgetPage";
import { AccountPage } from "./pages/Account/AccountPage";
import { BotsPage } from "./pages/Bots/BotsPage";
import {
  ErrorStore,
  ErrorStoreContext,
  initGlobalErrorStore,
} from "./stores/ErrorStore";
import { ErrorPopup } from "./components/ErrorPopup/ErrorPopup";
import StreamCreditsWidgetPage from "./pages/StreamCredits/StreamCreditsWidgetPage";
import AuctionWidgetPage from "./pages/AuctionWidget/AuctionWidgetPage";
import AuctionPage from "./pages/AuctionWidget/AuctionPage";
import CustomWidgetPage from "./pages/CustomWidget/CustomWidgetPage";
import { ApiPage } from "./pages/Api/ApiPage";
import { AuthProvider } from "./contexts/AuthContext";
import ChatWidgetV2Page from "./pages/ChatWidgetV2/ChatWidgetV2Page";
import EmoteWallWidgetPage from "./pages/EmoteWall/EmoteWallWidgetPage";
import { forwardEmotesToCache, registerEmoteCacheWorker } from "./emoteCacheWorker";

const errorStore = new ErrorStore();
initGlobalErrorStore(errorStore);

// window.onerror = (message, source, lineno, colno, error) => {
//   console.log("Handling window onerror");
//   errorStore.setError(
//     "Произошла ошибка. Попробуйте позже",
//     `${message}\n${source}:${lineno}:${colno}`,
//   );
//   return true;
// };
// //
// window.onunhandledrejection = (event) => {
//   console.log("Handling window onunhandledrejection");
//   errorStore.setError(
//     "Произошла ошибка. Попробуйте позже",
//     String(event.reason),
//   );
//   event.preventDefault();
// };

// axios.interceptors.response.use(
//   (response) => response,
//   (error) => {
//     const message = error.response?.data?.message ?? error.message;
//     errorStore.setError("Произошла ошибка. Попробуйте позже", message);
//     return Promise.reject(error);
//   },
// );

async function widgetSettingsLoader({
  params,
}: {
  params: Params<"widgetId">;
}): Promise<WidgetData> {
  const separateSession = new URLSearchParams(window.location.search).get(
    "separateSession",
  );
  const session =
    separateSession && params.widgetId
      ? await separateWidgetAuth(params.widgetId)
      : await auth();
  let settings = {};
  if (params.widgetId) {
    settings = await axios
      .get(
        `${process.env.REACT_APP_WIDGET_API_ENDPOINT}/widgets/${params.widgetId}`,
      )
      .then((json) => {
        return json.data;
      });
  }

  const conf = await config(session.id);
  setLoglevel(session.logLevels);
  log.debug({ configuration: conf });
  const widgetId = params.widgetId ?? "unknown";
  const recipientId = session.id ?? "unknown";
  const features = session.features ?? [];

  return { recipientId, settings, conf, widgetId, features };
}

const urlParams = new URLSearchParams(window.location.search);
const code = urlParams.get("code");
if (code) {
  localStorage.setItem("code", code);
}
const state = urlParams.get("state");
if (state) {
  localStorage.setItem("state", state);
}

function detectPage(path: string): Page {
  if (path.endsWith("payment-page")) {
    return Page.PAYMENTPAGE;
  }
  if (path.endsWith("loyalty-page")) {
    return Page.LOYALTY;
  }
  if (path.endsWith("history-page")) {
    return Page.HISTORY;
  }
  if (path.endsWith("automation-page")) {
    return Page.AUTOMATION;
  }
  if (path.endsWith("commands-rewards-page")) {
    return Page.COMMANDSREWARDS;
  }
  if (path.endsWith("gateways")) {
    return Page.GATEWAYS;
  }
  if (path.indexOf("guides") > 0) {
    return Page.GUIDES;
  }
  if (path.indexOf("integrations") > 0) {
    return Page.INTEGRATIONS;
  }
  if (path.indexOf("bots") > 0) {
    return Page.BOTS;
  }
  if (path.indexOf("api") > 0) {
    return Page.API;
  }
  if (path.endsWith("account")) {
    return Page.ACCOUNT;
  }
  return Page.WIDGETS;
}

function ConfigurationPageTemplate() {
  const { recipientId } = useLoaderData() as WidgetData;
  const location = useLocation();
  const page = detectPage(location.pathname);
  const [fontStore] = useState<FontStore>(() => new FontStore());
  const [widgetStore] = useState<WidgetStore>(() => new DefaultWidgetStore());
  const [paymentPageConfig] = useState<PaymentPageConfig>(
    () => new PaymentPageConfig(recipientId),
  );

  return (
    <>
      <Flex
        id="support-buttons"
        style={{
          position: "absolute",
          bottom: "12px",
          right: "15px",
          paddingTop: "36px",
        }}
        gap={9}
      >
        <UtilityButton
          onClick={() => window.open("https://yoomoney.ru/to/4100118300145014")}
        >
          Поддержать
        </UtilityButton>
        <UtilityButton
          onClick={() => window.open("https://t.me/+mM61pLIJkZM2Y2E6")}
        >
          Обратная связь
        </UtilityButton>
      </Flex>
      <AntHeader>
        <Header />
      </AntHeader>
      <Flex vertical className="newstyle">
        <Flex style={{ paddingTop: "18px" }}>
          <style
            dangerouslySetInnerHTML={{
              __html: `
        #root {
          min-height: 100vh;
          padding-bottom: 90px;
        }`,
            }}
          />
          <style
            dangerouslySetInnerHTML={{
              __html: `
        body {
          padding-left: 18px;
          padding-right: 18px;
        }`,
            }}
          />
          <style
            dangerouslySetInnerHTML={{
              __html: `
        body::before {
          content: "";
          position: fixed;
          left: 0;
          right: 0;
          z-index: -1;
          display: block;
          background-color: #2d3436;
          background-image: linear-gradient(345deg, #0c0d0e 0%, #000000 58%);
          width: 100%;
          height: 100%;
        }`,
            }}
          />
          <Toolbar page={page} />
          <Flex
            style={{
              marginLeft: "24px",
              width: "100%",
              marginRight: "24px",
              paddingBottom: "60px",
            }}
            className="full-width"
          >
            <Content
              className={`newstyle`}
              style={{
                paddingTop: "15px",
                borderRadius: "16px",
                backgroundColor: "var(--oda-color-100)",
              }}
            >
              <WarningsPanel />
              <FontContext.Provider value={fontStore}>
                <WidgetStoreContext.Provider value={widgetStore}>
                  <PaymentPageConfigContext.Provider value={paymentPageConfig}>
                    <Outlet />
                  </PaymentPageConfigContext.Provider>
                </WidgetStoreContext.Provider>
              </FontContext.Provider>
            </Content>
          </Flex>
          <div className="right-space" />
        </Flex>
      </Flex>
    </>
  );
}

function AuthLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/configuration/widgets" />,
  },
  {
    element: <AuthLayout />,
    children: [
      {
        path: "/configuration",
        element: <ConfigurationPageTemplate />,
        loader: widgetSettingsLoader,
        children: [
          {
            path: "widgets",
            element: <ConfigurationPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "payment-page",
            element: <PaymentPageConfigPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "history-page",
            element: <HistoryPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "automation-page",
            element: <AutomationPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "commands-rewards-page",
            element: <CommandsRewardsPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "loyalty-page",
            element: <LoyaltyPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "guides",
            element: <GuidesPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "integrations/*",
            element: <IntegrationsPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "bots",
            element: <BotsPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "api",
            element: <ApiPage />,
            loader: widgetSettingsLoader,
          },
          {
            path: "gateways",
            element: <PaymentGatewaysConfiguration />,
            loader: widgetSettingsLoader,
          },
          {
            path: "account",
            element: <AccountPage />,
            loader: widgetSettingsLoader,
          },
        ],
      },
      {
        path: "/payment-alerts/:widgetId",
        element: <PaymentAlertsPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/auction-widget/:widgetId",
        element: <AuctionWidgetPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/auction/:widgetId",
        element: <AuctionPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/media/:widgetId",
        element: <MediaWidget />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/payments/:widgetId",
        element: <HistoryWidgetPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/horizontal-events/:widgetId",
        element: <HorizontalEventsPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/player-info/:widgetId",
        element: (
          <WidgetWrapper>
            <PlayerInfo />
          </WidgetWrapper>
        ),
        loader: widgetSettingsLoader,
      },
      {
        path: "/player-popup/:widgetId",
        element: <PlayerPopupPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/donaton/:widgetId",
        element: <DonatonPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/canvas/:widgetId",
        element: <CanvasPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/rutonychat/:widgetId",
        element: <RutonyChatPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/player-control/:widgetId",
        element: <PlayerControl />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/donaters-top-list/:widgetId",
        element: <DonatersTopListPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/reel/:widgetId",
        element: <ReelWidgetPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/custom/:widgetId",
        element: <CustomWidgetPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/roulette/:widgetId",
        element: <RouletteWidgetPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/donation-timer/:widgetId",
        element: <DonationTimerPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/donationgoal/:widgetId",
        element: <DonationGoalPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/stream-credits/:widgetId",
        element: <StreamCreditsWidgetPage />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/chat/:widgetId",
        element: <ChatWidgetV2Page />,
        loader: widgetSettingsLoader,
      },
      {
        path: "/emote-wall/:widgetId",
        element: <EmoteWallWidgetPage />,
        loader: widgetSettingsLoader,
      },
    ],
  },
  {
    path: "/login",
    element: <Login />,
  },
]);

const rootElement = document.getElementById("root");
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <ErrorStoreContext.Provider value={errorStore}>
      <ConfigProvider
        theme={{
          algorithm: theme.darkAlgorithm,
        }}
      >
        <RouterProvider router={router} />
      </ConfigProvider>
      <ErrorPopup />
    </ErrorStoreContext.Provider>,
  );
}

registerEmoteCacheWorker();
forwardEmotesToCache();
